import React, { useCallback, useEffect, useState } from 'react';
import { getTourCategories, createTourCategory, deleteTourCategory } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Tags } from 'lucide-react';
import { toast } from 'sonner';

export const TOUR_TYPE_LABELS = { istanbul: 'İstanbul Turları', yurtici: 'Yurtiçi Turları', yurtdisi: 'Yurtdışı Turları' };

const useTourCategories = (tourType) => {
  const [categories, setCategories] = useState([]);
  const reload = useCallback(async () => {
    if (!tourType) return;
    try {
      const { data } = await getTourCategories(tourType);
      setCategories(data);
    } catch (error) {
      toast.error('Kategoriler yüklenemedi');
    }
  }, [tourType]);
  useEffect(() => { reload(); }, [reload]);
  return [categories, reload];
};

const AddCategoryInput = ({ tourType, onAdded }) => {
  const [name, setName] = useState('');
  const add = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createTourCategory({ name: trimmed, tour_type: tourType });
      toast.success('Kategori eklendi');
      setName('');
      onAdded(trimmed);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kategori eklenemedi');
    }
  };
  return (
    <div className="flex gap-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Yeni kategori adı"
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} data-testid="category-new-input" />
      <Button type="button" variant="outline" onClick={add} data-testid="category-add-button"><Plus size={16} /></Button>
    </div>
  );
};

export const CategorySelect = ({ tourType, value, onChange }) => {
  const [categories, reload] = useTourCategories(tourType);
  const names = categories.map(c => c.name);
  const options = value && !names.includes(value) ? [value, ...names] : names;
  return (
    <div className="space-y-2">
      <Select value={value || undefined} onValueChange={(v) => { if (v) onChange(v); }}>
        <SelectTrigger data-testid="tour-category-select"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
        <SelectContent>
          {options.map(name => <SelectItem key={name} value={name} data-testid={`tour-category-option-${name}`}>{name}</SelectItem>)}
        </SelectContent>
      </Select>
      <AddCategoryInput tourType={tourType} onAdded={(name) => { reload(); onChange(name); }} />
    </div>
  );
};

export const TourCategoryManager = ({ tourType, open, onOpenChange }) => {
  const [categories, reload] = useTourCategories(open ? tourType : null);
  const remove = async (cat) => {
    if (!window.confirm(`"${cat.name}" kategorisi silinsin mi?`)) return;
    try {
      await deleteTourCategory(cat.id);
      toast.success('Kategori silindi');
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silinemedi');
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="category-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tags size={20} /> {TOUR_TYPE_LABELS[tourType]} – Kategoriler</DialogTitle>
        </DialogHeader>
        <Label>Yeni kategori</Label>
        <AddCategoryInput tourType={tourType} onAdded={reload} />
        <ul className="divide-y border rounded-lg max-h-80 overflow-y-auto" data-testid="category-list">
          {categories.length === 0 && <li className="p-3 text-sm text-gray-500">Henüz kategori yok</li>}
          {categories.map(cat => (
            <li key={cat.name} className="flex items-center justify-between p-3" data-testid={`category-item-${cat.name}`}>
              <span className="text-sm font-medium">{cat.name}</span>
              {cat.in_use ? (
                <span className="text-xs text-gray-500">Turlarda kullanılıyor</span>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => remove(cat)} data-testid={`category-delete-${cat.name}`}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
